import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Factory as FactoryIcon,
  Mail as MailIcon,
  Send as SendIcon,
  AlertCircle as AlertCircleIcon,
  CheckCircle as CheckCircleIcon,
  ArrowLeft as ArrowLeftIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { authService } from "@/services/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import styles from "./ForgotPassword.module.css";

export default function ForgotPassword() {
  const { t } = useTranslation();
  usePageTitle(t("pageTitle.forgotPassword"));
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isFormValid = email.length > 0 && email.includes("@");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);
    try {
      const result = await authService.forgotPassword(email);
      if (result.success) {
        setSuccess(
          result.message ||
            t(
              "auth.forgot.success"
            ),
        );
        // Redirect to login after 3s, matching Vue behavior.
        setTimeout(() => navigate("/auth/login"), 3000);
      } else {
        setError(
          result.message || t("auth.forgot.error"),
        );
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("auth.forgot.error"),
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.forgotPasswordForm}>
      <div className={styles.formHeader}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}>
            <FactoryIcon size={32} />
          </div>
          <h1>{t("auth.forgot.title")}</h1>
        </div>
        <p className={styles.formSubtitle}>
          {t(
            "auth.forgot.subtitle"
          )}
        </p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="email" className={styles.formLabel}>
            {t("auth.forgot.emailLabel")}
          </label>
          <div className={styles.inputContainer}>
            <MailIcon size={20} className={styles.inputIcon} />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              placeholder={t(
                "auth.forgot.emailPlaceholder"
              )}
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>
        </div>

        <button
          type="submit"
          className={styles.submitBtn}
          disabled={isLoading || !isFormValid}
        >
          {isLoading ? <div className={styles.spinner} /> : <SendIcon size={20} />}
          {isLoading
            ? t("auth.forgot.sending")
            : t("auth.forgot.sendInstructions")}
        </button>

        {error ? (
          <div className={styles.errorMessage} role="alert">
            <AlertCircleIcon size={16} />
            {error}
          </div>
        ) : null}

        {success ? (
          <div className={styles.successMessage} role="status">
            <CheckCircleIcon size={16} />
            {success}
          </div>
        ) : null}
      </form>

      <div className={styles.formFooter}>
        <p>{t("auth.forgot.remembered")}</p>
        <Link to="/auth/login" className={styles.backLink}>
          <ArrowLeftIcon size={16} />
          {t("auth.forgot.backToLogin")}
        </Link>
      </div>
    </div>
  );
}
